// Google Cloud Text-to-Speech API 서비스
// 고품질 WaveNet 음성 생성

class GoogleTTSService {
  constructor() {
    // 클라이언트에서 직접 API 사용 (환경변수 접근)
    this.apiKey = process.env.REACT_APP_GEMINI_TOKEN;
    this.baseUrl = 'https://texttospeech.googleapis.com/v1/text:synthesize';
    this.isAvailable = !!this.apiKey;
    



    
    if (!this.isAvailable) {
      console.error('❌ Google TTS API 키가 없습니다. REACT_APP_GEMINI_TOKEN 환경변수를 확인해주세요.');
    }
  }

  // 텍스트를 음성으로 변환
  async synthesizeSpeech(text, options = {}) {
    if (!this.isAvailable) {
      throw new Error('Google API 키가 필요합니다.');
    }

    const requestBody = {
      input: {
        text: text
      },
      voice: {
        languageCode: options.languageCode || 'ko-KR',
        name: options.voiceName || 'ko-KR-Neural2-A', // 최신 Neural2 음성 (더 자연스러움)
        ssmlGender: options.gender || 'FEMALE'
      },
      audioConfig: {
        audioEncoding: 'MP3',
        speakingRate: options.speakingRate || 1.0, // 자연스러운 속도
        pitch: options.pitch || 0.0, // 자연스러운 피치
        volumeGainDb: options.volumeGain || 0.0,
        sampleRateHertz: 24000, // 고품질 샘플링 레이트
        effectsProfileId: options.effectsProfile || ['headphone-class-device']
      }
    };

    try {
      const response = await fetch(`${this.baseUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`TTS API 오류: ${error.error?.message || response.statusText}`);
      }

      const data = await response.json();
      return data.audioContent; // Base64 인코딩된 오디오 데이터

    } catch (error) {
      console.error('Google TTS 요청 실패:', error);
      throw error;
    }
  }

  // Base64 오디오를 재생 가능한 URL로 변환
  createAudioUrl(base64Audio) {
    const audioData = atob(base64Audio);
    const arrayBuffer = new ArrayBuffer(audioData.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    
    for (let i = 0; i < audioData.length; i++) {
      uint8Array[i] = audioData.charCodeAt(i);
    }
    
    const blob = new Blob([arrayBuffer], { type: 'audio/mp3' });
    return URL.createObjectURL(blob);
  }

  // 팟캐스트 스크립트 전체를 음성으로 변환
  async createPodcastAudio(script, options = {}, onProgress = null) {
    if (!this.isAvailable) {
      throw new Error('Google API 키가 필요합니다.');
    }

    // 스크립트를 청크로 분할 (API 제한: 5000자)
    const chunks = this.splitTextIntoChunks(script, 4800);
    const audioUrls = [];
    
    for (let i = 0; i < chunks.length; i++) {
      if (onProgress) {
        onProgress(i + 1, chunks.length, `청크 ${i + 1} 생성 중...`);
      }

      try {
        const base64Audio = await this.synthesizeSpeech(chunks[i], options);
        const audioUrl = this.createAudioUrl(base64Audio);
        audioUrls.push(audioUrl);
        
        // API 제한 방지를 위한 딜레이
        if (i < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
      } catch (error) {
        console.error(`청크 ${i + 1} 생성 실패:`, error);
        throw error;
      }
    }

    return audioUrls;
  }

  // 고정된 설정으로 팟캐스트 오디오 생성
  async createPodcastAudioWithConfig(script, ttsConfig, onProgress = null) {
    if (!this.isAvailable) {
      throw new Error('Google API 키가 필요합니다.');
    }

    // 스크립트를 청크로 분할 (API 제한: 5000자)
    const chunks = this.splitTextIntoChunks(script, 4800);
    const audioUrls = [];
    
    for (let i = 0; i < chunks.length; i++) {
      if (onProgress) {
        onProgress(i + 1, chunks.length, `청크 ${i + 1} 생성 중...`);
      }

      try {
        // 고정된 설정을 사용하여 직접 API 호출
        const requestBody = {
          input: {
            text: chunks[i]
          },
          voice: ttsConfig.voice,
          audioConfig: ttsConfig.audioConfig
        };

        const response = await fetch(`${this.baseUrl}?key=${this.apiKey}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(`TTS API 오류: ${error.error?.message || response.statusText}`);
        }

        const data = await response.json();
        const audioUrl = this.createAudioUrl(data.audioContent);
        audioUrls.push(audioUrl);
        
        // API 제한 방지를 위한 딜레이
        if (i < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
      } catch (error) {
        console.error(`청크 ${i + 1} 생성 실패:`, error);
        throw error;
      }
    }

    return audioUrls;
  }

  // 텍스트를 청크로 분할
  splitTextIntoChunks(text, maxLength = 4800) {
    const sentences = text.split(/[.!?]\s+/);
    const chunks = [];
    let currentChunk = '';

    for (const sentence of sentences) {
      if (currentChunk.length + sentence.length < maxLength) {
        currentChunk += (currentChunk ? '. ' : '') + sentence;
      } else {
        if (currentChunk) chunks.push(currentChunk + '.');
        currentChunk = sentence;
      }
    }
    
    if (currentChunk) chunks.push(currentChunk);
    return chunks.filter(chunk => chunk.trim().length > 0);
  }

  // 사용 가능한 한국어 음성 목록
  getAvailableKoreanVoices() {
    return [
      // 최신 Neural2 음성 (가장 자연스러움)
      {
        name: 'ko-KR-Neural2-A',
        gender: 'FEMALE',
        description: '🌟 최고급 여성 음성 (Neural2) - 가장 자연스러움',
        quality: 'Ultra Premium'
      },
      {
        name: 'ko-KR-Neural2-B',
        gender: 'FEMALE',
        description: '🌟 부드러운 여성 음성 (Neural2)',
        quality: 'Ultra Premium'
      },
      {
        name: 'ko-KR-Neural2-C',
        gender: 'MALE',
        description: '🌟 최고급 남성 음성 (Neural2) - 가장 자연스러움',
        quality: 'Ultra Premium'
      },
      // WaveNet 음성
      {
        name: 'ko-KR-WaveNet-A',
        gender: 'FEMALE',
        description: '자연스러운 여성 음성 (WaveNet)',
        quality: 'Premium'
      },
      {
        name: 'ko-KR-WaveNet-B',
        gender: 'FEMALE', 
        description: '부드러운 여성 음성 (WaveNet)',
        quality: 'Premium'
      },
      {
        name: 'ko-KR-WaveNet-C',
        gender: 'MALE',
        description: '자연스러운 남성 음성 (WaveNet)',
        quality: 'Premium'
      },
      {
        name: 'ko-KR-WaveNet-D',
        gender: 'MALE',
        description: '깊은 남성 음성 (WaveNet)',
        quality: 'Premium'
      },
      // Standard 음성
      {
        name: 'ko-KR-Standard-A',
        gender: 'FEMALE',
        description: '표준 여성 음성',
        quality: 'Standard'
      },
      {
        name: 'ko-KR-Standard-C',
        gender: 'MALE',
        description: '표준 남성 음성',
        quality: 'Standard'
      }
    ];
  }

  // 팟캐스트용 최적화된 설정 프리셋
  getPodcastPresets() {
    return {
      professional: {
        voiceName: 'ko-KR-Neural2-C', // 최고급 남성 전문가 음성
        speakingRate: 1.0,
        pitch: 0.0,
        description: '🌟 전문적이고 자연스러운 남성 음성 (Neural2)'
      },
      friendly: {
        voiceName: 'ko-KR-Neural2-A', // 최고급 친근한 여성 음성
        speakingRate: 0.95,
        pitch: 0.0,
        description: '🌟 친근하고 자연스러운 여성 음성 (Neural2)'
      },
      warm: {
        voiceName: 'ko-KR-Neural2-B', // 따뜻한 여성 음성
        speakingRate: 0.9,
        pitch: 1.0,
        description: '🌟 따뜻하고 부드러운 여성 음성 (Neural2)'
      },
      classic_professional: {
        voiceName: 'ko-KR-WaveNet-C', // 클래식 전문가 음성
        speakingRate: 0.9,
        pitch: -1.0,
        description: '신뢰감 있는 남성 음성 (WaveNet)'
      },
      classic_friendly: {
        voiceName: 'ko-KR-WaveNet-A', // 클래식 친근한 음성
        speakingRate: 0.95,
        pitch: 0.0,
        description: '친근한 여성 음성 (WaveNet)'
      }
    };
  }

  // API 사용량 추정
  estimateUsage(text) {
    const characterCount = text.length;
    // Google Cloud TTS 가격 (한국 기준, WaveNet)
    const pricePerMillion = 16; // USD
    const estimatedCost = (characterCount / 1000000) * pricePerMillion;
    
    return {
      characters: characterCount,
      estimatedCostUSD: estimatedCost.toFixed(4),
      estimatedCostKRW: (estimatedCost * 1300).toFixed(0)
    };
  }

  // MP3 파일 다운로드
  downloadAudio(audioUrl, filename = 'podcast.mp3') {
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // 여러 오디오 파일을 ZIP으로 다운로드 (선택사항)
  async downloadMultipleAudios(audioUrls, baseFilename = 'podcast') {
    audioUrls.forEach((url, index) => {
      setTimeout(() => {
        this.downloadAudio(url, `${baseFilename}_part${index + 1}.mp3`);
      }, index * 500); // 0.5초 간격으로 다운로드
    });
  }
}

// 싱글톤 인스턴스
const googleTTSService = new GoogleTTSService();

export default googleTTSService;